// src/pages/VoiceLanding.js
//
// Public landing page for Blackmount Voice (PR 4 of iOS-app-separation).
// Lives at /voice-landing for public visitors (the authenticated /voice
// route renders <Voices /> — see App.js). Hero copy mirrors the App Store
// page for narrative consistency.
//
// Pre-rendered by react-snap (configured in package.json). Keep the markup
// SSR-safe — no window references at top level.

import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { APP_STORE_URL } from '../config/extensionUrls';

export default function VoiceLanding() {
  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      color: 'text.primary',
    }}>
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, flex: 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.25rem', md: '3.5rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mb: 3,
            }}
          >
            Talk into Blackmount. It remembers.
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.05rem', md: '1.25rem' },
              fontWeight: 400,
              color: 'text.secondary',
              maxWidth: 640,
              mx: 'auto',
              mb: 5,
            }}
          >
            Voice notes, conversations, transcripts, summaries, and actions.
            Capture what matters, search it later, turn it into reminders or tasks.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener"
              sx={{ px: 4, py: 1.25, fontSize: '1rem', textTransform: 'none' }}
            >
              Get Blackmount Voice
            </Button>
            <Button
              component={RouterLink}
              to="/signup?source=voice"
              variant="outlined"
              size="large"
              sx={{ px: 4, py: 1.25, fontSize: '1rem', textTransform: 'none' }}
            >
              Try on the web
            </Button>
          </Box>

          <Box sx={{ mt: 8 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              What you get
            </Typography>
            <Box component="ul" sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              maxWidth: 480,
              mx: 'auto',
              textAlign: 'left',
            }}>
              {[
                'Record voice notes — solo thoughts or full conversations',
                'Automatic transcripts with optional speaker diarization',
                'AI-suggested action items, reminders, and follow-ups',
                'Searchable across every recording, by keyword or topic',
                'Multilingual — 90+ languages supported',
              ].map((line) => (
                <Typography key={line} component="li" variant="body1" sx={{ color: 'text.primary' }}>
                  • {line}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>

      <Box component="footer" sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          © {new Date().getFullYear()} Blackmount. ·{' '}
          <RouterLink to="/" style={{ color: 'inherit' }}>Home</RouterLink>{' '}·{' '}
          <RouterLink to="/explore" style={{ color: 'inherit' }}>Explore</RouterLink>
        </Typography>
      </Box>
    </Box>
  );
}
